import React, {
    useCallback, useEffect, useRef, useReducer
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
    Flex, Text, Box, Icon, Link
} from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import { colors, space } from 'style/config';
import Actions from 'Actions';
import userLocationUtils from 'utils/UserLocation';
import userUtils from 'utils/User';
import AddressUtils from 'utils/Address';
import Empty from 'constants/empty';
import localeUtils from 'utils/LanguageLocale';
import { userSelector } from 'selectors/user/userSelector';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';

const { userSubscriptionsSelector } = UserSubscriptionsSelector;
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import ZipCodeLocatorEN from 'components/ProductPage/Type/DigitalProduct/SameDayUnlimitedLandingPage/ZipCodeLocator/locales/ZipCodeLocator_en_US';
import ZipCodeLocatorFR from 'components/ProductPage/Type/DigitalProduct/SameDayUnlimitedLandingPage/ZipCodeLocator/locales/ZipCodeLocator_fr_CA';

const { formatZipCode } = AddressUtils;
const { isUS } = localeUtils;
const getText = localeUtils.getLocalisedDataFromResource(ZipCodeLocatorEN, ZipCodeLocatorFR);

const ANALYTICS_MAP = {
    unavailable: () => ({
        method: anaConsts.LINK_TRACKING_EVENT,
        prop55: `${anaConsts.PAGE_NAMES.SAME_DAY_UNLIMITED}:${anaConsts.PAGE_DETAIL.ZIP_CODE_UNAVAILABLE}`
    })
};

function fireAnalytics(type) {
    const { method, prop55 } = ANALYTICS_MAP[type]();
    pdpAnalytics.trackEvent(method, {
        data: {
            ...(method === anaConsts.LINK_TRACKING_EVENT && { actionInfo: prop55 })
        }
    });
}

const ZIP_INITIAL = { isSDUAvailable: false, zipCodeUpdated: null, hasPreferredZipCode: false };

function zipCodeReducer(state, action) {
    switch (action.type) {
        case 'SET_HAS_PREFERRED':
            return { ...state, hasPreferredZipCode: action.payload };
        case 'UPDATE_ZIP':
            return { isSDUAvailable: action.sameDayAvailable, zipCodeUpdated: action.zipCode, hasPreferredZipCode: true };
        default:
            return state;
    }
}

const ZipCodeLocator = ({ isCanada, updateZipCodeEligible, fromChooseOptionsModal }) => {
    const reduxDispatch = useDispatch();
    const user = useSelector(userSelector);
    const userSubscriptions = useSelector(userSubscriptionsSelector);

    const showShippingDeliveryLocationModal = useCallback(opts => reduxDispatch(Actions.showShippingDeliveryLocationModal(opts)), [reduxDispatch]);

    // Locale strings
    const SDUAvailable = getText('SDUAvailable');
    const SDUUnavailable = getText('SDUUnavailable');
    const yourLocation = getText('yourLocation');
    const noSephoraLocations = getText('noSephoraLocations');
    const tapToSeeAvailability = getText('tapToSeeAvailability');
    const checkAnotherPostalCode = getText('checkAnotherPostalCode');
    const checkAnotherZIP = getText('checkAnotherZIP');

    // Derived values (mirroring the original HOC combiner verbatim)
    const isCA = isCanada || !isUS();
    const { preferredZipCode } = user;
    const zipCode = preferredZipCode ? formatZipCode(preferredZipCode) : yourLocation;
    const checkOtherZipCode = isCA ? checkAnotherPostalCode : checkAnotherZIP;
    const SDUSubscription = userSubscriptions?.filter(subscription => subscription.type === 'SDU') || Empty.Array;
    const isUserSDUTrialEligible = userUtils.isAnonymous()
        ? true
        : SDUSubscription.length > 0 && SDUSubscription[0].isTrialEligible && SDUSubscription[0].status === 'INACTIVE';

    const [state, dispatch] = useReducer(zipCodeReducer, ZIP_INITIAL);
    const isFirstRenderRef = useRef(true);
    const prevPreferredZipRef = useRef(undefined);
    const updateZipCodeEligibleRef = useRef(updateZipCodeEligible);
    updateZipCodeEligibleRef.current = updateZipCodeEligible;

    useEffect(() => {
        dispatch({ type: 'SET_HAS_PREFERRED', payload: !!preferredZipCode });

        if (preferredZipCode && (isFirstRenderRef.current || prevPreferredZipRef.current !== preferredZipCode)) {
            userLocationUtils.updatePreferredZipCode({ postalCode: zipCode, skipZipCodeUpdate: true }).then(res => {
                dispatch({ type: 'UPDATE_ZIP', sameDayAvailable: res.sameDayAvailable, zipCode: res.zipCode });
                updateZipCodeEligibleRef.current({ sameDayAvailable: res.sameDayAvailable, zipCode });

                if (!res.sameDayAvailable) {
                    fireAnalytics('unavailable');
                }
            });
        }

        isFirstRenderRef.current = false;
        prevPreferredZipRef.current = preferredZipCode;
    }, [preferredZipCode, zipCode]);

    const openZipCodeLocator = () => {
        showShippingDeliveryLocationModal({
            isOpen: true,
            options: { skipZipCodeUpdate: !fromChooseOptionsModal },
            callback: ({ sameDayAvailable, zipCode: updatedZip }) => {
                dispatch({ type: 'UPDATE_ZIP', sameDayAvailable, zipCode: updatedZip });
                updateZipCodeEligibleRef.current({ sameDayAvailable, zipCode: updatedZip });
            },
            sduZipcodeModal: true,
            isUserSDUTrialEligible
        });

        digitalData.page.attributes.previousPageData.pageName = 'same-day unlimited:enter zip code:n/a:*';
    };

    const { zipCodeUpdated, hasPreferredZipCode, isSDUAvailable } = state;

    const link = (
        <Link
            css={styles.bold}
            onClick={openZipCodeLocator}
            arrowDirection='down'
            children={hasPreferredZipCode ? zipCodeUpdated : yourLocation}
        />
    );

    return (
        <Box css={styles.container}>
            <div
                css={`
                    ${hasPreferredZipCode ? { ...styles.zipCodeContainer } : { ...styles.zipCodeContainer, ...styles.noPreferredZipCode }}
                `}
            >
                <Flex
                    css={`
                        ${hasPreferredZipCode && !isSDUAvailable && styles.spacer}
                    `}
                >
                    {isSDUAvailable ? (
                        <Flex alignItems='center'>
                            <Flex css={{ ...styles.checkmark, ...styles.icon }}>
                                <Icon
                                    css={styles.checkmarkIcon}
                                    name='checkmark'
                                />
                            </Flex>
                            <Box
                                ml={2}
                                mr={1}
                            >
                                {SDUAvailable}
                            </Box>
                            {link}
                        </Flex>
                    ) : (
                        <Flex alignItems='center'>
                            {hasPreferredZipCode && (
                                <Icon
                                    name='alert'
                                    css={{ ...styles.icon, ...styles.warning }}
                                />
                            )}
                            <Box
                                ml={hasPreferredZipCode ? 2 : 0}
                                mr={1}
                            >
                                {hasPreferredZipCode ? SDUUnavailable : tapToSeeAvailability}
                            </Box>
                            {link}
                        </Flex>
                    )}
                </Flex>
            </div>
            {!hasPreferredZipCode || isSDUAvailable || (
                <Text
                    is='p'
                    css={styles.subtitle}
                >
                    {`${noSephoraLocations} `}
                    <Link
                        onClick={openZipCodeLocator}
                        children={checkOtherZipCode}
                        underline={true}
                        color='blue'
                    />
                </Text>
            )}
        </Box>
    );
};

const styles = {
    container: {
        padding: `${space[3]}px`,
        border: `${space[2]}px`,
        borderColor: `${colors.midGray}`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: `${space[1]}px`
    },
    zipCodeContainer: {
        position: 'relative',
        padding: '0 0 0 25px'
    },
    noPreferredZipCode: {
        padding: '0'
    },
    spacer: {
        marginBottom: `${space[2]}px`
    },
    icon: {
        borderRadius: '50%'
    },
    checkmarkIcon: {
        fontSize: '1.25em',
        padding: `${space[1]}px`,
        size: '0.65em'
    },
    checkmark: {
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'full',
        backgroundColor: `${colors.green}`,
        color: `${colors.white}`,
        lineHeight: '1em'
    },
    warning: {
        fontSize: '1.25em',
        color: `${colors.red}`
    },
    bold: {
        fontWeight: 'bold'
    },
    subtitle: {
        display: 'block',
        color: `${colors.gray}`
    }
};

ZipCodeLocator.propTypes = {
    isCanada: PropTypes.bool,
    updateZipCodeEligible: PropTypes.func,
    fromChooseOptionsModal: PropTypes.bool
};

export default wrapFunctionalComponent(ZipCodeLocator, 'ZipCodeLocator');
